import useTerms from "../../../hooks/useTerms";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Drawer, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import {
  Spa as SpaIcon,
  CloseRounded as CloseIcon,
  AddRounded as AddIcon,
  CheckCircleOutline as OkIcon,
  ErrorOutline as WarnIcon,
  PhoneIphoneRounded as PhoneIcon,
} from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, FormTextField, FormSelect, FormDatePicker, ChoiceChipGroup } from "../../atoms";
import DrawerField from "../../molecules/DrawerField";
import BodyMap, { VIEW_LABEL } from "../../molecules/BodyMap";
import { tileInputProps } from "../../molecules/InfoTile";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  useCheckPracticeSessionMutation,
  useCreatePracticeSessionMutation,
  useUpdatePracticeSessionMutation,
  useListConsentTemplatesMutation,
} from "../../../store/traditionalApi";
import ConsentRecordDrawer from "./ConsentRecordDrawer";
import { useClinicModule } from "../../../hooks/useClinicModule";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useListPatientPackagesMutation } from "../../../store/billingApi";
import { EMPTY_RECOMMENDATION, formToPayload, sessionToForm, formatDateTr } from "./practiceSessionFormat";

const STATUS_OPTIONS = [
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal edildi" },
];
// Onay bekleyen seans elle "Tamamlandı" yapılamaz (hasta onayı ya da "Kağıtla onayla")
const PENDING_STATUS_OPTIONS = [
  { value: "pending_consent", label: "Onay bekliyor" },
  { value: "cancelled", label: "İptal edildi" },
];
const AUTO_TEMPLATE = "";

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

const GROUP_LABEL_SX = {
  fontSize: TYPO.caption,
  fontWeight: 600,
  color: COLORS.slate500,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  gridColumn: "1 / -1",
  mt: 1,
};

const textInput = (palette, value, onChange, extra = {}) => (
  <FormTextField
    palette={palette}
    variant="standard"
    size="small"
    fullWidth
    value={value}
    onChange={(e) => onChange(e.target.value)}
    InputProps={tileInputProps(true)}
    {...extra}
  />
);

/**
 * Uygunluk uyarısı + onam durumu kutusu (seçilen uygulama türüne göre).
 * `mobileConsent` ("new" | "pending"): hasta uygulamayı kullanıyor — onay telefona
 * gider; onam kuralı (engelle/uyar) ve satır içi "Onam kaydet" uygulanmaz.
 */
function CheckPanel({ check, loading, canManageConsent, onRecordConsent, palette, mobileConsent }) {
  const terms = useTerms();
  if (loading) {
    return <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>Uygunluk ve onam kontrol ediliyor…</Typography>;
  }
  if (!check) return null;
  const { matches = [], consent = {} } = check;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {matches.length > 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2, fontSize: TYPO.body, "& .MuiAlert-message": { width: "100%" } }}>
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, mb: 0.5 }}>Uygunluk uyarısı</Typography>
          {matches.map((m) => (
            <Typography key={m.text} sx={{ fontSize: TYPO.label }}>
              • {m.text}
            </Typography>
          ))}
          <Typography sx={{ fontSize: TYPO.caption, mt: 0.75, color: COLORS.warningDarker }}>
            Uyarı kaydı engellemez; karar uygulayıcıdadır. Görülen uyarılar seans kaydına yazılır.
          </Typography>
        </Alert>
      ) : (
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
          Hastanın kronik hastalık, alerji ve aktif ilaçlarında bu uygulama için uyarı bulunmadı.
        </Typography>
      )}

      {mobileConsent ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(palette?.primary || COLORS.slate700, 0.06),
          }}
        >
          <PhoneIcon sx={{ fontSize: 18, color: palette?.primary || COLORS.slate600 }} />
          <Typography sx={{ flex: 1, minWidth: 180, fontSize: TYPO.label, color: COLORS.slate700 }}>
            {mobileConsent === "pending"
              ? `Onay ${terms.patientGenLower} telefonunda bekliyor; uygulama türü değişirse istek yeniden gönderilir.`
              : `Onay ${terms.patientGenLower} telefonuna gönderilecek; ${terms.patientLower} onaylayınca seans tamamlanır.`}
          </Typography>
        </Box>
      ) : (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: consent.satisfied ? alpha(COLORS.success, 0.08) : alpha(COLORS.warning, 0.1),
        }}
      >
        {consent.satisfied ? (
          <OkIcon sx={{ fontSize: 18, color: COLORS.successDark }} />
        ) : (
          <WarnIcon sx={{ fontSize: 18, color: COLORS.warningDark }} />
        )}
        <Typography sx={{ flex: 1, minWidth: 180, fontSize: TYPO.label, color: COLORS.slate700 }}>
          {!consent.required
            ? consent.record
              ? `Onam gerekmiyor · kayıtlı onam: ${consent.record.title} (${formatDateTr(consent.record.signedAt)})`
              : "Bu uygulama onam gerektirmiyor."
            : consent.satisfied
            ? `Onam var: ${consent.record?.title || "Onam"} · ${formatDateTr(consent.record?.signedAt)}`
            : consent.willBlock
            ? "Onam gerekli — kliniğinizin ayarına göre onamsız seans kaydedilemez."
            : `Onam gerekli — ${terms.patientGenLower} kayıtlı onamı yok; seans uyarıyla kaydedilir.`}
        </Typography>
        {!consent.satisfied && canManageConsent ? (
          <Button
            size="small"
            onClick={onRecordConsent}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: TYPO.label, color: palette?.primary, px: 1.5 }}
          >
            Onam kaydet
          </Button>
        ) : null}
      </Box>
      )}
    </Box>
  );
}

/**
 * Seans editörü (yeni / düzenle). Uygulama türü seçilince sunucudan uygunluk
 * uyarıları ve onam durumu alınır; eksik onam drawer içinden kaydedilebilir.
 *
 * Hasta uygulamayı kullanıyorsa (`patientUsesApp`) yeni seans "Onay bekliyor"
 * açılır ve onay isteği telefona gider (şablon otomatik ya da seçilen).
 *
 * @param {object|null} session - null → yeni seans
 * @param {Array} practices - etkin uygulama türleri
 * @param {boolean} [patientUsesApp] - hasta mobil uygulamaya giriş yapmış
 * @param {(session, warnings) => void} onSaved
 */
export default function PracticeSessionDrawer({
  open,
  onClose,
  token,
  patientId,
  clinicId,
  patientName,
  session,
  practices = [],
  canManageConsent,
  patientUsesApp = false,
  onSaved,
  onConsentRecorded,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const isNew = !session?._id;
  const isPending = session?.status === "pending_consent";
  // Onay telefona gider: yeni seans (uygulama kullanan hasta) ya da zaten onay bekleyen seans
  const mobileConsent = isNew ? (patientUsesApp ? "new" : null) : isPending ? "pending" : null;

  const [form, setForm] = useState(() => sessionToForm(session));
  const [check, setCheck] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [runCheck, { isLoading: checking }] = useCheckPracticeSessionMutation();
  const [createSession, { isLoading: creating }] = useCreatePracticeSessionMutation();
  const [updateSession, { isLoading: updating }] = useUpdatePracticeSessionMutation();
  const saving = creating || updating;

  // Seans paketinden düş (billing modülü açık ve paket okuma yetkisi varsa; yalnız yeni seansta)
  const billingModule = useClinicModule("billing");
  const canReadPackages = useHasPermission("packages:read");
  const billingOn = billingModule && canReadPackages;
  const [listPackages] = useListPatientPackagesMutation();
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState("");
  useEffect(() => {
    if (!open || !isNew || !billingOn || !patientId) return;
    setPackageId("");
    listPackages({ token, patientId, usableOnly: true })
      .unwrap()
      .then((r) => setPackages(r?.data?.packages || []))
      .catch(() => setPackages([]));
  }, [open, isNew, billingOn, patientId, token, listPackages]);

  useEffect(() => {
    if (open) {
      setForm(sessionToForm(session));
      setCheck(null);
    }
  }, [open, session]);

  // Telefona gidecek onam şablonu (yalnız yeni seans + uygulama kullanan hasta); boş = otomatik
  const [listTemplates] = useListConsentTemplatesMutation();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(AUTO_TEMPLATE);
  useEffect(() => {
    if (!open || !isNew || !patientUsesApp || !token) return;
    setTemplateId(AUTO_TEMPLATE);
    let cancelled = false;
    listTemplates({ token, ...(clinicId ? { clinicId } : {}) })
      .unwrap()
      .then((res) => !cancelled && setTemplates((res?.data?.items || []).filter((t) => t.isActive !== false)))
      .catch(() => !cancelled && setTemplates([]));
    return () => {
      cancelled = true;
    };
  }, [open, isNew, patientUsesApp, token, clinicId, listTemplates]);

  // Seçilen uygulamaya uyan şablonlar: genel + bu uygulamaya özel
  const templateOptions = useMemo(() => {
    const fitting = templates.filter((t) => !idOf(t.practice) || idOf(t.practice) === form.practiceId);
    return [
      { value: AUTO_TEMPLATE, label: "Otomatik seç (önerilen)" },
      ...fitting.map((t) => ({
        value: t._id,
        label: `${t.title}${t.practice?.name ? ` · ${t.practice.name}` : " · Genel"}${t.isSystem ? " (sistem)" : ""}`,
      })),
    ];
  }, [templates, form.practiceId]);
  useEffect(() => {
    if (templateId && !templateOptions.some((o) => o.value === templateId)) setTemplateId(AUTO_TEMPLATE);
  }, [templateId, templateOptions]);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const scope = clinicId ? { clinicId } : {};
  const doCheck = useCallback(
    async (practiceId) => {
      if (!practiceId || !token) {
        setCheck(null);
        return;
      }
      try {
        const res = await runCheck({ token, patientId, practiceId, ...(clinicId ? { clinicId } : {}) }).unwrap();
        setCheck(res?.data || null);
      } catch (_) {
        setCheck(null);
      }
    },
    [token, patientId, clinicId, runCheck]
  );

  useEffect(() => {
    if (open) doCheck(form.practiceId);
  }, [open, form.practiceId, doCheck]);

  // Silinmiş/pasif tür düzenlenen seansta seçili kalabilsin
  const practiceOptions = useMemo(() => {
    const opts = practices.map((p) => ({ value: p._id, label: p.name }));
    if (session?.practice && !opts.some((o) => o.value === form.practiceId) && form.practiceId) {
      opts.push({ value: form.practiceId, label: session.practiceName || "Uygulama" });
    }
    return opts;
  }, [practices, session, form.practiceId]);

  const updateRegion = (i, label) =>
    set({ regions: form.regions.map((r, idx) => (idx === i ? { ...r, label } : r)) });
  const removeRegion = (i) => set({ regions: form.regions.filter((_, idx) => idx !== i) });
  const updateRec = (i, patch) =>
    set({ recommendations: form.recommendations.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });

  const willBlock = !mobileConsent && Boolean(check?.consent?.willBlock) && form.status === "completed";
  const canSubmit = Boolean(form.practiceId && form.date) && !(isNew && willBlock);

  const handleSave = async () => {
    if (!canSubmit) return;
    const body = {
      token,
      ...scope,
      ...formToPayload(form),
      cautionsAcknowledged: (check?.matches || []).map((m) => m.text),
    };
    try {
      const res = isNew
        ? await createSession({
            ...body,
            patientId,
            ...(packageId ? { patientPackageId: packageId } : {}),
            ...(mobileConsent && templateId ? { consentTemplateId: templateId } : {}),
          }).unwrap()
        : await updateSession({
            ...body,
            sessionId: session._id,
            // "pending_consent" istemciden gönderilmez; sunucu yalnız completed/cancelled kabul eder
            ...(form.status !== "pending_consent" ? { status: form.status } : {}),
          }).unwrap();
      onSaved?.(res?.data?.session, res?.data?.warnings || []);
    } catch (e) {
      showError(e?.data?.message || "Seans kaydedilemedi.");
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={saving ? undefined : onClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 640 }, display: "flex", flexDirection: "column", bgcolor: COLORS.white } }}
      >
        <DrawerHeader
          icon={<SpaIcon />}
          title={isNew ? "Yeni Seans" : "Seansı Düzenle"}
          subtitle={patientName || "Geleneksel uygulama seansı"}
          onClose={onClose}
        />

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, rowGap: 2.5, columnGap: 2 }}>
            <DrawerField label="Uygulama türü *" fullWidth>
              <FormSelect
                value={form.practiceId}
                onChange={(v) => set({ practiceId: v })}
                options={practiceOptions}
                placeholder="Uygulama seçin"
                variant="standard"
                size="small"
                fullWidth
                InputProps={tileInputProps(true)}
              />
            </DrawerField>

            {form.practiceId ? (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <CheckPanel
                  check={check}
                  loading={checking && !check}
                  canManageConsent={canManageConsent}
                  onRecordConsent={() => setConsentOpen(true)}
                  palette={palette}
                  mobileConsent={mobileConsent}
                />
              </Box>
            ) : null}

            {mobileConsent === "new" && form.practiceId && templates.length > 0 ? (
              <DrawerField
                label="Onam şablonu"
                fullWidth
                hint={`Otomatik seçimde uygulamaya özel şablon önceliklidir; ${terms.patientLower} metni telefonunda okuyup onaylar.`}
              >
                <FormSelect
                  value={templateId}
                  onChange={(v) => setTemplateId(v || AUTO_TEMPLATE)}
                  options={templateOptions}
                  placeholder={null}
                  variant="standard"
                  size="small"
                  fullWidth
                  InputProps={tileInputProps(true)}
                />
              </DrawerField>
            ) : null}

            <DrawerField label="Tarih *">
              <FormDatePicker value={form.date} onChange={(v) => set({ date: v })} variant="standard" fullWidth clearable={false} />
            </DrawerField>
            <DrawerField label="Saat">
              {textInput(palette, form.time, (v) => set({ time: v }), { type: "time", inputProps: { "aria-label": "Saat" } })}
            </DrawerField>
            <DrawerField label="Süre (dk)">
              {textInput(palette, form.durationMin, (v) => set({ durationMin: v }), {
                type: "number",
                placeholder: "—",
                inputProps: { min: 0, max: 1440, "aria-label": "Süre (dk)" },
              })}
            </DrawerField>
            {isNew && billingOn && packages.length > 0 ? (
              <DrawerField label="Paketten düş">
                <FormSelect
                  palette={palette}
                  variant="standard"
                  size="small"
                  fullWidth
                  value={packageId}
                  onChange={(v) => setPackageId(v || "")}
                  options={[
                    { value: "", label: "Düşme" },
                    ...packages.map((p) => ({
                      value: p._id,
                      label: `${p.template?.name || "Paket"} · ${p.remaining ?? Math.max(0, (p.total || 0) - (p.used || 0))} hak`,
                    })),
                  ]}
                  placeholder={null}
                />
              </DrawerField>
            ) : null}
            {!isNew ? (
              <DrawerField
                label="Durum"
                hint={isPending ? `${terms.patient} onaylayınca ya da “Kağıtla onayla” ile tamamlanır.` : undefined}
              >
                <ChoiceChipGroup
                  options={isPending ? PENDING_STATUS_OPTIONS : STATUS_OPTIONS}
                  value={form.status}
                  onChange={(v) => set({ status: v })}
                  palette={palette}
                  ariaLabel="Seans durumu"
                  sx={{ mt: 0.5 }}
                />
              </DrawerField>
            ) : null}

            <Typography sx={GROUP_LABEL_SX}>Vücut haritası</Typography>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <BodyMap value={form.regions} onChange={(regions) => set({ regions })} figureWidth={160} />
              {form.regions.length > 0 ? (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {form.regions.map((r, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ width: 64, flexShrink: 0, fontSize: TYPO.label, color: COLORS.slate500 }}>
                        {i + 1} · {VIEW_LABEL[r.view]}
                      </Typography>
                      {textInput(palette, r.label || "", (v) => updateRegion(i, v), {
                        placeholder: "Bölge notu (ör. C7, sağ kürek)",
                        inputProps: { maxLength: 120, "aria-label": `${i + 1}. nokta notu` },
                      })}
                      <Tooltip title="Noktayı kaldır">
                        <IconButton size="small" aria-label={`${i + 1}. noktayı kaldır`} onClick={() => removeRegion(i)}>
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>

            <Typography sx={GROUP_LABEL_SX}>Seans notları</Typography>
            <DrawerField label="Kullanılan malzemeler" fullWidth>
              {textInput(palette, form.materials, (v) => set({ materials: v }), { multiline: true, minRows: 1, placeholder: "Ör. 6 kupa, steril lanset" })}
            </DrawerField>
            <DrawerField label="Gözlem" fullWidth>
              {textInput(palette, form.observation, (v) => set({ observation: v }), { multiline: true, minRows: 2 })}
            </DrawerField>
            <DrawerField label={`${terms.patient} geri bildirimi`} fullWidth>
              {textInput(palette, form.patientFeedback, (v) => set({ patientFeedback: v }), { multiline: true, minRows: 1 })}
            </DrawerField>

            <Box sx={{ ...GROUP_LABEL_SX, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Takviye / bitkisel öneriler</span>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => set({ recommendations: [...form.recommendations, { ...EMPTY_RECOMMENDATION }] })}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: TYPO.label, color: palette?.primary, px: 1.5 }}
              >
                Öneri ekle
              </Button>
            </Box>
            {form.recommendations.length === 0 ? (
              <Typography sx={{ gridColumn: "1 / -1", fontSize: TYPO.label, color: COLORS.slate500 }}>
                Öneri eklenmedi. Öneriler reçete yerine geçmez; ilaç kataloğuna bağlı değildir.
              </Typography>
            ) : (
              form.recommendations.map((r, i) => (
                <Box
                  key={i}
                  sx={{
                    gridColumn: "1 / -1",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1.3fr 1fr 0.8fr auto" },
                    columnGap: 1.5,
                    rowGap: 1,
                    alignItems: "end",
                    pb: 1.25,
                    borderBottom: `1px solid ${COLORS.slate100}`,
                  }}
                >
                  <DrawerField label="Ad *">{textInput(palette, r.name, (v) => updateRec(i, { name: v }), { placeholder: "Ör. Çörek otu yağı" })}</DrawerField>
                  <DrawerField label="Kullanım">{textInput(palette, r.usage, (v) => updateRec(i, { usage: v }), { placeholder: "Günde 1 tatlı kaşığı" })}</DrawerField>
                  <DrawerField label="Süre">{textInput(palette, r.duration, (v) => updateRec(i, { duration: v }), { placeholder: "1 ay" })}</DrawerField>
                  <Tooltip title="Öneriyi kaldır">
                    <IconButton
                      size="small"
                      aria-label="Öneriyi kaldır"
                      onClick={() => set({ recommendations: form.recommendations.filter((_, idx) => idx !== i) })}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    {textInput(palette, r.note, (v) => updateRec(i, { note: v }), { placeholder: "Not (opsiyonel)" })}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <DrawerFooter
          onCancel={onClose}
          cancelLabel="Vazgeç"
          onSubmit={handleSave}
          submitLabel={isNew ? (mobileConsent ? "Kaydet ve onaya gönder" : "Seansı Kaydet") : "Kaydet"}
          loadingLabel="Kaydediliyor..."
          loading={saving}
          disabled={!canSubmit}
          fullWidth
        />
      </Drawer>

      <ConsentRecordDrawer
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        token={token}
        patientId={patientId}
        clinicId={clinicId}
        patientName={patientName}
        practiceId={form.practiceId}
        onSaved={(record) => {
          onConsentRecorded?.(record);
          doCheck(form.practiceId);
        }}
      />
    </>
  );
}
